'use client';

import * as React from 'react';

import { cn } from '../lib/utils';
import { safeHttpUrl } from '../lib/safe-url';
import { BrandMark, type BrandService } from './brand-mark';

const SERVICE_LABELS: Record<BrandService, string> = {
  notion: 'Notion',
  linear: 'Linear',
  github: 'GitHub',
  slack: 'Slack',
};

type LinkChipProps = Omit<
  React.AnchorHTMLAttributes<HTMLAnchorElement>,
  'children'
> & { href: string } & (
    | {
        service: BrandService;
        /** If set, chip renders as a bordered pill with mark + label.
         *  If omitted, renders a mark-only compact chip (list-row usage). */
        label?: string;
        icon?: never;
      }
    | {
        /** Generic pill: any icon + label. `mailto:` links are allowed and open in place. */
        icon: React.ReactNode;
        label: string;
        service?: never;
      }
  );

const MAILTO = /^mailto:/i;

/**
 * External link chip for a known service (Notion / Linear / GitHub / Slack), or a
 * generic `icon` + `label` pill. http(s) links open in a new tab with
 * `rel="noopener noreferrer"`; clicks stop propagating so the chip can nest inside clickable rows.
 */
export function LinkChip({
  href,
  service,
  label,
  icon,
  className,
  onClick,
  ...props
}: LinkChipProps) {
  const handleClick: React.MouseEventHandler<HTMLAnchorElement> = (e) => {
    e.stopPropagation();
    onClick?.(e);
  };

  const mailto = !service && MAILTO.test(href);
  const safeHref = mailto ? href : safeHttpUrl(href);
  const mark = service ? (
    <BrandMark service={service} aria-hidden="true" />
  ) : (
    icon
  );

  if (label) {
    if (!safeHref) {
      return (
        <span
          className={cn(
            'inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1 text-xs opacity-60',
            className,
          )}
        >
          {mark}
          {label}
        </span>
      );
    }
    return (
      <a
        href={safeHref}
        {...(mailto ? {} : { target: '_blank', rel: 'noopener noreferrer' })}
        onClick={handleClick}
        className={cn(
          'inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1 text-xs transition-colors',
          'hover:border-[var(--l3-violet)] hover:bg-[var(--l3-violet-soft)] hover:text-[var(--l3-violet)]',
          'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--l3-violet)]',
          className,
        )}
        {...props}
      >
        {mark}
        {label}
      </a>
    );
  }

  // compact (mark-only) — ~26px tap target from `p-1.5` + size-3.5 icon.
  // A generic chip always has a label, so only branded chips reach here.
  if (!service) return null;
  if (!safeHref) {
    return (
      <span
        aria-label={SERVICE_LABELS[service]}
        className={cn(
          'inline-flex items-center justify-center rounded-sm p-1.5 opacity-30',
          className,
        )}
      >
        <BrandMark service={service} aria-hidden="true" />
      </span>
    );
  }
  return (
    <a
      href={safeHref}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={SERVICE_LABELS[service]}
      onClick={handleClick}
      className={cn(
        'inline-flex items-center justify-center rounded-sm p-1.5 opacity-60 transition-opacity',
        'hover:opacity-100',
        'focus-visible:opacity-100 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--l3-violet)]',
        className,
      )}
      {...props}
    >
      <BrandMark service={service} aria-hidden="true" />
    </a>
  );
}

export type { LinkChipProps };
